import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Pause, Square, Clock, Save, Plus, Car } from 'lucide-react';
import { formatTime, timeToHours, getCurrentDateDutch } from '@/lib/utils';
import { toast } from 'sonner';

const TIMER_STORAGE_KEY = 'urenbuddy_timer';

export default function TimerPage() {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('uren');
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  
  const [saveData, setSaveData] = useState({
    client_id: '',
    beschrijving: '',
    factureerbaar: true,
    // Mileage fields
    van_locatie: '',
    naar_locatie: '',
    kilometers: ''
  });
  
  const intervalRef = useRef(null);

  useEffect(function() {
    fetchClients();
    loadTimerState();
    
    return function() {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(function() {
    saveTimerState();
  }, [isRunning, isPaused, seconds, startTime]);

  async function fetchClients() {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (err) {
      console.error('Could not fetch clients');
    }
  }

  function loadTimerState() {
    try {
      const saved = localStorage.getItem(TIMER_STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.isRunning && !state.isPaused && state.startTime) {
          const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
          setSeconds(state.seconds + elapsed);
          setIsRunning(true);
          setIsPaused(false);
          setStartTime(Date.now());
          startInterval();
        } else if (state.isPaused) {
          setSeconds(state.seconds);
          setIsRunning(true);
          setIsPaused(true);
        }
      }
    } catch (err) {
      console.error('Could not load timer state');
    }
  }

  function saveTimerState() {
    try {
      const state = { isRunning, isPaused, seconds, startTime };
      localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      console.error('Could not save timer state');
    }
  }

  function startInterval() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(function() {
      setSeconds(function(s) { return s + 1; });
    }, 1000);
  }

  function handleStart() {
    setIsRunning(true);
    setIsPaused(false);
    setStartTime(Date.now());
    startInterval();
  }

  function handlePause() {
    setIsPaused(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }

  function handleResume() {
    setIsPaused(false);
    setStartTime(Date.now());
    startInterval();
  }

  function handleStop() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowSaveDialog(true);
  }

  function handleReset() {
    setIsRunning(false);
    setIsPaused(false);
    setSeconds(0);
    setStartTime(null);
    if (intervalRef.current) clearInterval(intervalRef.current);
    localStorage.removeItem(TIMER_STORAGE_KEY);
  }

  async function handleSave() {
    if (!saveData.client_id) {
      toast.error(t('timer.noClientSelected'));
      return;
    }

    try {
      // Save time entry
      const hours = timeToHours(seconds);
      await api.post('/time-entries', {
        client_id: saveData.client_id,
        datum: getCurrentDateDutch(),
        uren: hours,
        beschrijving: saveData.beschrijving || t('timer.timerRegistration'),
        factureerbaar: saveData.factureerbaar,
        uurtarief: user?.standaard_uurtarief || 75
      });
      toast.success(t('timer.timerSaved'));

      // Also save mileage if filled in
      if (saveData.van_locatie && saveData.naar_locatie && saveData.kilometers) {
        await api.post('/mileage-entries', {
          client_id: saveData.client_id,
          datum: getCurrentDateDutch(),
          van_locatie: saveData.van_locatie,
          naar_locatie: saveData.naar_locatie,
          kilometers: parseFloat(saveData.kilometers),
          km_tarief: user?.standaard_km_tarief || 0.23,
          beschrijving: saveData.beschrijving || t('timer.timerTrip')
        });
        toast.success(t('timer.kilometersSaved'));
      }

      setShowSaveDialog(false);
      handleReset();
      setSaveData({ client_id: '', beschrijving: '', factureerbaar: true, van_locatie: '', naar_locatie: '', kilometers: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  }

  function handleDiscard() {
    setShowSaveDialog(false);
    handleReset();
    setSaveData({ client_id: '', beschrijving: '', factureerbaar: true, van_locatie: '', naar_locatie: '', kilometers: '' });
  }

  async function handleAddClient() {
    if (!newClientName.trim()) {
      toast.error(t('timer.enterClientName'));
      return;
    }
    try {
      const res = await api.post('/clients', { naam: newClientName.trim() });
      setClients(function(prev) { return [...prev, res.data]; });
      setSaveData(function(prev) { return { ...prev, client_id: res.data.client_id }; });
      setNewClientName('');
      setShowAddClientDialog(false);
      toast.success(t('clients.clientSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="timer-page">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold font-heading text-foreground">{t('timer.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('timer.subtitle')}</p>
      </div>

      <Card className="border-0 shadow-lg">
        <CardContent className="pt-12 pb-12">
          <div className="text-center mb-12">
            <div className="timer-display text-foreground" data-testid="timer-display">
              {formatTime(seconds)}
            </div>
            {isRunning && !isPaused && (
              <p className="text-sm text-green-600 mt-2 flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                {t('timer.running')}
              </p>
            )}
            {isPaused && (
              <p className="text-sm text-amber-600 mt-2 flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full" />
                {t('timer.paused')}
              </p>
            )}
          </div>

          <div className="flex justify-center gap-4">
            {!isRunning ? (
              <Button
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white px-8 h-14 text-lg rounded-xl btn-active"
                onClick={handleStart}
                data-testid="timer-start-btn"
              >
                <Play className="w-6 h-6 mr-2" />
                {t('timer.start')}
              </Button>
            ) : (
              <div className="flex gap-4">
                {isPaused ? (
                  <Button
                    size="lg"
                    className="bg-green-500 hover:bg-green-600 text-white px-8 h-14 text-lg rounded-xl btn-active"
                    onClick={handleResume}
                    data-testid="timer-resume-btn"
                  >
                    <Play className="w-6 h-6 mr-2" />
                    {t('timer.resume')}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    variant="outline"
                    className="px-8 h-14 text-lg rounded-xl border-2"
                    onClick={handlePause}
                    data-testid="timer-pause-btn"
                  >
                    <Pause className="w-6 h-6 mr-2" />
                    {t('timer.pause')}
                  </Button>
                )}
                <Button
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 h-14 text-lg rounded-xl btn-active"
                  onClick={handleStop}
                  data-testid="timer-stop-btn"
                >
                  <Square className="w-6 h-6 mr-2" />
                  {t('timer.stopAndSave')}
                </Button>
              </div>
            )}
          </div>

          {isRunning && (
            <div className="mt-8 pt-6 border-t text-center">
              <p className="text-sm text-muted-foreground">
                {t('timer.estimatedHours')}: <span className="font-semibold text-foreground">{timeToHours(seconds).toFixed(2)}</span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm bg-blue-50 dark:bg-blue-950">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">{t('timer.tip')}</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t('timer.tipText')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Dialog with Tabs */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('timer.saveRegistration')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-muted rounded-xl">
              <p className="text-sm text-muted-foreground">{t('timer.recordedTime')}</p>
              <p className="text-3xl font-mono font-bold text-foreground mt-1">{formatTime(seconds)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                = {timeToHours(seconds).toFixed(2)} {t('common.hours').toLowerCase()}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('common.client')} *</Label>
              <div className="flex gap-2">
                <Select value={saveData.client_id} onValueChange={function(v) { setSaveData(function(prev) { return { ...prev, client_id: v }; }); }}>
                  <SelectTrigger className="flex-1" data-testid="timer-client-select">
                    <SelectValue placeholder={t('timer.selectClient')} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(function(client) {
                      return <SelectItem key={client.client_id} value={client.client_id}>{client.naam}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={function() { setShowAddClientDialog(true); }} data-testid="add-client-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('common.description')}</Label>
              <Textarea
                placeholder={t('timer.addDescription')}
                value={saveData.beschrijving}
                onChange={function(e) { setSaveData(function(prev) { return { ...prev, beschrijving: e.target.value }; }); }}
                data-testid="timer-description-input"
              />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="uren" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t('timer.hoursOnly')}
                </TabsTrigger>
                <TabsTrigger value="uren-km" className="flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  + {t('common.kilometers')}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="uren-km" className="space-y-4 mt-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700 mb-3 font-medium">{t('timer.addKilometersForClient')}:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm">{t('registrations.fromLocation')}</Label>
                      <Input
                        placeholder="Amsterdam"
                        value={saveData.van_locatie}
                        onChange={function(e) { setSaveData(function(prev) { return { ...prev, van_locatie: e.target.value }; }); }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">{t('registrations.toLocation')}</Label>
                      <Input
                        placeholder="Rotterdam"
                        value={saveData.naar_locatie}
                        onChange={function(e) { setSaveData(function(prev) { return { ...prev, naar_locatie: e.target.value }; }); }}
                      />
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <Label className="text-sm">{t('common.kilometers')}</Label>
                    <Input
                      type="number"
                      placeholder="50"
                      value={saveData.kilometers}
                      onChange={function(e) { setSaveData(function(prev) { return { ...prev, kilometers: e.target.value }; }); }}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleDiscard} data-testid="timer-discard-btn">
              {t('common.cancel')}
            </Button>
            <Button 
              className="bg-cyan-500 hover:bg-cyan-600"
              onClick={handleSave}
              data-testid="timer-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('clients.addClient')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('clients.name')} *</Label>
              <Input
                placeholder={t('timer.clientNamePlaceholder')}
                value={newClientName}
                onChange={function(e) { setNewClientName(e.target.value); }}
                data-testid="new-client-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowAddClientDialog(false); }}>{t('common.cancel')}</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={handleAddClient}>
              <Plus className="w-4 h-4 mr-2" />
              {t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
